import { useEffect } from 'react';
import { store, useAppDispatch, useAppSelector } from '../redux/store';
import { fetchPartnerBranding, PartnerBranding } from '../redux/slices/partnerBranding';
import { PARTNER_BRANDING_FLAG } from '../constants/partnerBranding';

export type { PartnerBranding };

// safe-regex flags the nested quantifier on star height alone, but this cannot
// backtrack: every repetition of the outer group must consume a literal `-`, which
// `[a-z0-9]+` can never match, so each input has exactly one possible partition.
// eslint-disable-next-line security/detect-unsafe-regex
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const readSlugFromUrl = (): string => {
	try {
		const slug = new URLSearchParams(window.location.search).get('org') || '';
		return slug.trim().toLowerCase();
	} catch {
		return '';
	}
};

// The `?org=` slug in the current URL is the single source of truth for which
// branding to show: a valid slug renders that partner's logo, a different slug
// renders different branding, and its absence renders the default Pearsana logo.
// Returns '' when no valid slug is present.
const resolveValidSlug = (): string => {
	const slug = readSlugFromUrl();
	if (!slug || !SLUG_PATTERN.test(slug) || slug.length > 63) {
		return '';
	}
	return slug;
};

const SLUG_STORAGE_KEY = 'pearsana.partnerSlug';

/**
 * The co-branded slug for the current sign-up journey.
 *
 * `?org=` is only present on the landing URL, but post-sign-up screens (profile,
 * onboarding) still need it to disambiguate a user who appears on more than one
 * roster — so the first valid slug seen is cached for the tab's session.
 * Returns `undefined` when there is no slug, so callers can omit the param.
 */
export const readPartnerSlug = (): string | undefined => {
	const fromUrl = resolveValidSlug();
	try {
		if (fromUrl) {
			sessionStorage.setItem(SLUG_STORAGE_KEY, fromUrl);
			return fromUrl;
		}
		return sessionStorage.getItem(SLUG_STORAGE_KEY) || undefined;
	} catch {
		return fromUrl || undefined;
	}
};

/**
 * Kicks off the partner-branding fetch as early as possible (call at app
 * bootstrap). When the co-branded link (`?org=<slug>`) is present this dispatches
 * the public branding fetch (which also preloads the logo image) in parallel
 * with feature flags, so the logo is ready to paint by the time the auth screen
 * mounts — eliminating the lazy, post-mount fetch delay. No-op when no valid
 * slug is present, so default sign-ins pay no cost.
 */
export const prefetchPartnerBranding = (): void => {
	const slug = resolveValidSlug();
	if (!slug) return;
	// Persist for the post-sign-up screens, which no longer carry `?org=`.
	readPartnerSlug();
	store.dispatch(fetchPartnerBranding(slug));
};

/**
 * Resolves the partner branding for the account-creation screens from Redux.
 *
 * The `?org=<slug>` query param in the current URL is the single source of
 * truth for *which* branding to show, and the `partner-branding-enabled` feature
 * flag decides *whether* to show it at all. Branding is fetched via the
 * `partnerBranding` slice and memoised per slug in the store, so re-mounts — e.g.
 * toggling the Sign In / Sign Up tab — reuse the same logo URL without
 * re-fetching or reloading the image.
 *
 * Crucially, the feature flags load asynchronously at bootstrap. Until they
 * resolve we can't tell "flag off" from "flag not loaded yet", so when a
 * co-branded slug is present we optimistically treat branding as expected and
 * report `loading`. This lets the caller show a placeholder spinner in the logo
 * slot during the flag-loading window instead of flashing the default Pearsana
 * logo and then swapping in the partner logo a few seconds later.
 *
 * `expectsBranding` is true whenever a valid slug is present and the feature has
 * not been positively determined to be off, so the caller keeps the logo slot in
 * a loading state until either the partner logo or the default logo is final.
 */
export const usePartnerBranding = () => {
	const dispatch = useAppDispatch();
	const slug = resolveValidSlug();

	// Feature-flag state. `flagPending` is the window before the flags settle,
	// during which the flag value is unknown (not yet "off"). On a flag-fetch
	// failure `flagsLoading` clears without `flagsLoaded`, so `flagPending`
	// becomes false and we fall back to the default logo rather than spinning
	// forever.
	const enabled = useAppSelector((state) => Boolean(state.featureFlags.flags[PARTNER_BRANDING_FLAG]));
	const flagsLoading = useAppSelector((state) => state.featureFlags.loading);
	const flagsLoaded = useAppSelector((state) => state.featureFlags.loaded);
	const flagPending = flagsLoading && !flagsLoaded;

	const branding = useAppSelector((state) => (slug ? state.partnerBranding.brandingBySlug[slug] ?? null : null));
	// Resolved once the slug has an entry in the store (even a null "no branding"
	// result), at which point the branding fetch is no longer in flight.
	const brandingResolved = useAppSelector((state) =>
		slug ? Object.prototype.hasOwnProperty.call(state.partnerBranding.brandingBySlug, slug) : true
	);

	useEffect(() => {
		if (!slug) return;
		// Idempotent per slug (guarded in the thunk): a no-op when already resolved
		// or in flight, so switching tabs never triggers a redundant fetch. The
		// dispatch is independent of the flag so the logo is ready the instant the
		// flag resolves to on.
		dispatch(fetchPartnerBranding(slug));
	}, [slug, dispatch]);

	// A co-branded slug is present and we haven't determined the feature is off.
	const expectsBranding = Boolean(slug) && (flagPending || enabled);
	// Keep the slot in a loading state while the flag is still resolving, or while
	// the flag is on but the branding fetch hasn't returned yet.
	const loading = Boolean(slug) && (flagPending || (enabled && !brandingResolved));

	return {
		branding: enabled && slug ? branding : null,
		loading,
		expectsBranding
	};
};
